'use client';
import { PropsWithChildren, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import Modals from './Modals';
import VideoModalTrigger from './VideoModalTrigger';
import GalleryModalTrigger from './GalleryModalTrigger';
import GalleryModalImages from './GalleryModalImages';

import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import { Village } from '@/services/types';
import { removeBodyScrollbar } from '@/utils/helpers';
import {
  getVillageDealPromotionTitle,
  getVillageVisiblePromotions,
} from '@/utils/village';
import VillagePromotionLabel from '../VillagePromotionLabel';

const PROMOTION_BADGE_HEIGHT = 45;
const PROMOTION_BADGE_GAP = 8;

type Dictionary = {
  regionZoneDict: IRegionZoneDictonary;
  villageDict: IVillageDictionary;
};

type Props = {
  village: Village;
  dict: Dictionary;
  lang: string;
} & PropsWithChildren;

export default function VillageMedia({ dict, village, lang, children }: Props) {
  const { villageDict } = dict;
  const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
  const [galleryModalIsOpen, setGalleryModalIsOpen] = useState(false);
  const [openModalCarousel, setOpenModalCarousel] = useState(false);

  const showDesktopTriggers = village.videoUrl;

  const visiblePromotions = getVillageVisiblePromotions(village.deals)
    .map((deal) => getVillageDealPromotionTitle(deal, lang))
    .filter((title): title is string => Boolean(title));

  function openVideoModal() {
    setVideoModalIsOpen(true);
  }

  function closeVideoModal() {
    setVideoModalIsOpen(false);
  }

  function openGalleryModal() {
    setGalleryModalIsOpen(true);
  }

  function closeGalleryModal() {
    setGalleryModalIsOpen(false);
  }

  const galleryMedia = village.media.filter(
    (file) => file.type === 'gallery' || file.type === 'cover',
  );

  useEffect(() => {
    removeBodyScrollbar(galleryModalIsOpen);
  }, [galleryModalIsOpen]);

  useEffect(() => {
    removeBodyScrollbar(videoModalIsOpen);
  }, [videoModalIsOpen]);

  return (
    <div
      id="villages-medias-section"
      className="w-full px-sm md:px-md desktop:px-lg max-w-default mx-auto lg:mx-0 desktop:max-w-full">
      <div className="margin-bottom-responsive">
        <div className="mx-[-0.625rem] flex md:mx-[-0.9375rem]">
          <div
            className={cn(
              'w-[calc(100%-1.25rem)] mx-[0.625rem] flex-[0_0_auto] md:w-[calc(100%-1.875rem)] md:mx-[0.9375rem]',
              {
                'desktop:w-[calc(66.66667%-1.875rem)]': village.videoUrl,
              },
            )}>
            <div className="mx-[-0.9375rem] relative min-h-[550px] h-[60vh] max-h-[900px] md:mx-[-2.5rem] desktop:mx-0">
              <Modals
                videoModalIsOpen={videoModalIsOpen}
                galleryModalIsOpen={galleryModalIsOpen}
                openModalCarousel={openModalCarousel}
                openVideoModal={openVideoModal}
                openGalleryModal={openGalleryModal}
                closeVideoModal={closeVideoModal}
                closeGalleryModal={closeGalleryModal}
                dict={dict}
                village={village}>
                {galleryModalIsOpen && (
                  <GalleryModalImages
                    lang={lang}
                    villageMedia={village.media}
                    setOpenModalCarousel={setOpenModalCarousel}
                  />
                )}
              </Modals>
              {!showDesktopTriggers && (
                <div
                  onClick={openGalleryModal}
                  className="hidden desktop:flex cursor-pointer flex-col items-center justify-center absolute top-0 left-0 w-full h-full p-[30px] text-white before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[100px] before:bg-[linear-gradient(180deg,transparent,rgba(0,0,0,.6))] before:z-10">
                  <div className="w-full mt-auto flex items-center flex-wrap text-white z-10">
                    <h2 className="mb-0 mt-0 text-4xl leading-[1.25] font-title">
                      {galleryMedia.length} {villageDict.gallery.photos}
                    </h2>
                    <p className="mb-0 ml-auto font-semibold uppercase text-left leading-[1.7] tracking-[2px]">
                      {villageDict.gallery.viewAll}
                    </p>
                  </div>
                </div>
              )}
              {children}
              {village.isNew && (
                <VillagePromotionLabel
                  isNew
                  isPromotion={false}
                  newVillaLabel={dict.villageDict.newVilla}
                />
              )}
              {!village.isNew && visiblePromotions.length > 0 && (
                <div className="absolute top-0 left-0 right-0 z-[1]">
                  {visiblePromotions.map((title, index) => (
                    <div
                      key={`${title}-${index}`}
                      className="relative w-full"
                      style={{
                        height: PROMOTION_BADGE_HEIGHT + PROMOTION_BADGE_GAP,
                      }}>
                      <VillagePromotionLabel
                        isPromotion
                        isNew={false}
                        promotionTitle={title}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {showDesktopTriggers && (
            <div className="hidden desktop:flex flex-col flex-[0_0_auto] w-[calc(33.33333%-1.875rem)] mx-[0.9375rem] relative min-h-[550px] h-[60vh] max-h-[900px]">
              <VideoModalTrigger
                handleOpenModal={openVideoModal}
                villageMedia={village.media}
              />
              <GalleryModalTrigger
                villageDict={villageDict}
                handleOpenModal={openGalleryModal}
                village={village}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
